import classnames from 'classnames';
import type { ProductSelectorTabType } from 'components/shared/ProductAndClientSelector/productAndClientSelector.model';
import { Tab } from 'components/shared/Tabs/Tab';
import { Tabs } from 'components/shared/Tabs/Tabs';
import type { AnalyticCategoryActions } from 'models/analytics';
import type { SelectedProductVariation } from 'models/productsReferential';
import { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { AppState } from 'state/state.model';
import { workspaceProductSelectorTabSelected } from 'state/workspaces/workspaces/workspace.actions';
import type { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { getSelectedProductSelectorTab } from 'state/workspaces/workspaces/workspaces.selector';

import { ProductCategoryPicker } from './ProductCategoryPicker';
import { ProductMostRecentList } from './ProductMostRecentList';
import { ProductPinnedList } from './ProductPinnedList';

interface ProductSelectorTabsProps {
  workspace: WorkspaceWithProductSelector;
  selectedProductVariation: Partial<SelectedProductVariation>;
  canSelectPayoffType?: boolean;
  className?: string;
  onProductVariationSelected(productVariation: Partial<SelectedProductVariation>, isQuickStart: boolean): void;
}

export const ProductSelectorTabs: FC<ProductSelectorTabsProps> = ({
  workspace,
  selectedProductVariation,
  canSelectPayoffType,
  className,
  onProductVariationSelected,
}) => {
  const dispatch = useDispatch();
  const activeSelectorTabType = useSelector((state: AppState) => getSelectedProductSelectorTab(state, workspace));

  const setActiveTab = useCallback(
    (tabType: ProductSelectorTabType) => {
      dispatch(workspaceProductSelectorTabSelected(workspace, tabType));
      logAnalytics.trackEvent(
        `Primary market product selector ${workspace}` as keyof AnalyticCategoryActions,
        'Change tab',
        tabType,
      );
    },
    [dispatch, workspace],
  );

  return (
    <Tabs
      className={classnames('overflow-hidden', className)}
      id="productSelector.tabs"
      navFill
      boxShadowBorder
      activeTabId={activeSelectorTabType}
      onTabActivated={id => setActiveTab(id as ProductSelectorTabType)}
      navClassName="nav nav-tabs nav-lg nav-fill nav-justified rounded-1 rounded-bottom border-0"
      tabPaneClassName="h-100"
    >
      <Tab
        tabId="catalogue"
        label="productSelector.tab.catalogue"
        e2eId="productSelector.tab.catalogue"
        className="rounded-1 rounded-bottom"
      >
        <ProductCategoryPicker
          workspace={workspace}
          canSelectPayoffType={canSelectPayoffType}
          selectedProductVariation={selectedProductVariation}
          onProductVariationSelected={onProductVariationSelected}
        />
      </Tab>
      <Tab
        tabId="pinned"
        label="productSelector.tab.pinned"
        e2eId="productSelector.tab.pinned"
        className="rounded-1 rounded-bottom"
      >
        <ProductPinnedList
          workspace={workspace}
          selectedProductVariation={selectedProductVariation}
          onProductVariationSelected={onProductVariationSelected}
        />
      </Tab>
      <Tab
        tabId="mostRecent"
        label="productSelector.tab.mostRecent"
        e2eId="productSelector.tab.mostRecent"
        className="rounded-1 rounded-bottom"
      >
        <ProductMostRecentList
          workspace={workspace}
          selectedProductVariation={selectedProductVariation}
          onProductVariationSelected={onProductVariationSelected}
        />
      </Tab>
    </Tabs>
  );
};
